import { Leaf, ChevronDown } from "lucide-react";
import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

// Yesterday's date — subtly reinforces the "same blank page as yesterday" framing below
const yesterdayLabel = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(d);
})();

/**
 * The opening is the product, not a description of it.
 * The visitor reads a journal entry, receives the companion response,
 * and only then sees the Eden Pages name.
 * They feel the product before they know what it is.
 */
export default function OpeningSection() {
  return (
    <section
      className="min-h-screen flex flex-col items-center justify-center relative px-6 sm:px-10"
      style={{ background: C.cream, paddingTop: 120, paddingBottom: 100 }}
    >
      <div className="max-w-[560px] mx-auto w-full">

        {/* Date — the top of a journal entry */}
        <FadeIn delay={0.15}>
          <p
            className="font-heading italic mb-12"
            style={{ fontSize: 14, color: C.txt3, letterSpacing: "0.5px" }}
          >
            {yesterdayLabel}
          </p>
        </FadeIn>

        {/* The journal entry — voice of the reader, not the product */}
        <FadeIn delay={0.3}>
          <p
            className="font-heading italic text-spec-brown"
            style={{ fontSize: "clamp(20px, 2.8vw, 28px)", lineHeight: 1.92 }}
          >
            The same blank page as yesterday.
            <br /><br />
            I opened my Bible, read a verse I've read a hundred times, and
            felt nothing. Closed it. Made coffee. Wondered if this is what
            doubt feels like, or just exhaustion.
          </p>
        </FadeIn>

        {/* The companion response — appears after the entry */}
        <FadeIn delay={0.6}>
          <div
            className="mt-12"
            style={{ borderLeft: `2px solid ${C.sageBdr}`, paddingLeft: 24 }}
          >
            <p
              className="font-ui mb-2.5"
              style={{ fontSize: 11, color: C.sage, letterSpacing: "1.3px" }}
            >
              PASTOR &nbsp;·&nbsp; REFLECTION
            </p>
            <p
              className="font-ui text-spec-txt2"
              style={{ fontSize: 16, lineHeight: 1.8 }}
            >
              You showed up anyway. That's not emptiness — that's faithfulness
              without feeling. Both matter.
            </p>
          </div>
        </FadeIn>

        {/* Brand reveal — the product name appears only after you've felt the product */}
        <FadeIn delay={0.9}>
          <div
            className="flex flex-col items-center gap-2 mt-20 pb-4"
          >
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 38,
                height: 38,
                background: C.sageGlow,
                border: `1px solid ${C.sageBdr}`,
              }}
            >
              <Leaf size={17} className="text-spec-sage" />
            </div>
            <span
              className="font-heading font-semibold text-spec-brown"
              style={{ fontSize: 18, letterSpacing: "0.3px" }}
            >
              Eden Pages
            </span>
          </div>
        </FadeIn>

      </div>

      {/* Scroll invitation — decorative, hidden from assistive technology */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-[bounce_2s_infinite]" aria-hidden="true">
        <ChevronDown size={18} className="text-spec-txt3" />
      </div>
    </section>
  );
}
